import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { GROUP_CHAT_HUB_HTTP_URL } from "../../config/serviceConfig";
import { getPreferredLanguage } from "../../config/languages";
import "./RoomLobby.css";

const deriveJoinViewMode = (detail) => {
  if (!detail || detail.displayMode !== "expo") return "normal";
  if (detail.expoLayout === "comprehensive") return "comprehensive";
  return "expo";
};

/**
 * 群聊大厅：创建房间 / 加入房间 / 房间列表
 * 支持模板选择、角色选择、多人状态同步
 */
const RoomLobby = ({
  currentUser,
  onCreateRoom,
  onJoinRoom,
  onResumeRoom,
  onViewArchive,
  isCreating,
  isJoining,
  error,
  clearError,
  displayMode = "normal",
  onDisplayModeChange,
  expoLayout = "classic",
  onExpoLayoutChange,
  scriptMode = "",
  onScriptModeChange,
  initialTemplateId = null,
}) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState("create"); // create | join | history
  const [templates, setTemplates] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(initialTemplateId || null);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [roomDetail, setRoomDetail] = useState(null);
  const [templateDetail, setTemplateDetail] = useState(null);
  const [selectedRoleName, setSelectedRoleName] = useState("");
  const [selectedCreateRoleName, setSelectedCreateRoleName] = useState("");
  const [joinViewMode, setJoinViewMode] = useState("normal");
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingTemplateDetail, setLoadingTemplateDetail] = useState(false);
  const [myRooms, setMyRooms] = useState([]);
  const [loadingMyRooms, setLoadingMyRooms] = useState(false);
  const [resumeDisplayMode, setResumeDisplayMode] = useState("normal");
  const [templateSearch, setTemplateSearch] = useState("");
  const [showOtherTemplates, setShowOtherTemplates] = useState(false);
  const [idleDevices, setIdleDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const joinableRooms = rooms.filter((r) => r.canJoin === true);
  const historicalRooms = rooms.filter((r) => r.canJoin !== true);
  const myJoinableRooms = myRooms.filter((r) => r.canJoin === true);
  const myHistoricalRooms = myRooms.filter((r) => r.canJoin !== true);
  const selectedTemplate = templates.find((tpl) => String(tpl.id) === String(selectedTemplateId));
  const recordingWhitelist = ["张三", "詹志强", "王胜胜", "张贞辉", "刘嘉斌", "刘祯頔", "刘严老师", "刘业楼", "刘师果"];

  const categoryLabel = (category) => {
    const labels = {
      engineering: "工程实践",
      innovation: "创新创业",
      design: "设计思维",
      science_expo: "科普展示",
    };
    return labels[String(category || "").toLowerCase()] || "其他";
  };

  const normalizedTemplateSearch = templateSearch.trim().toLowerCase();
  const filteredTemplates = useMemo(() => {
    if (!normalizedTemplateSearch) return templates;
    return templates.filter((tpl) => [
      tpl.displayName,
      tpl.name,
      tpl.description,
      tpl.category,
      ...(tpl.courseScopes || []),
    ].filter(Boolean).join(" ").toLowerCase().includes(normalizedTemplateSearch));
  }, [templates, normalizedTemplateSearch]);

  const courseTemplateGroups = useMemo(() => {
    const groups = new Map();
    filteredTemplates
      .filter((tpl) => tpl.scopeType === "course")
      .sort((a, b) => (a.displayName || a.name || "").localeCompare(b.displayName || b.name || "", "zh-CN"))
      .forEach((tpl) => {
        const courseNames = tpl.courseScopes?.length ? tpl.courseScopes : ["已加入课程"];
        courseNames.forEach((courseName) => {
          if (!groups.has(courseName)) groups.set(courseName, new Map());
          groups.get(courseName).set(String(tpl.id), tpl);
        });
      });
    return [...groups.entries()].map(([courseName, templatesByID]) => [courseName, [...templatesByID.values()]]);
  }, [filteredTemplates]);

  const otherTemplateGroups = useMemo(() => {
    const groups = new Map();
    filteredTemplates
      .filter((tpl) => tpl.scopeType !== "course")
      .sort((a, b) => (a.displayName || a.name || "").localeCompare(b.displayName || b.name || "", "zh-CN"))
      .forEach((tpl) => {
        const category = categoryLabel(tpl.category);
        if (!groups.has(category)) groups.set(category, []);
        groups.get(category).push(tpl);
      });
    return [...groups.entries()];
  }, [filteredTemplates]);

  useEffect(() => {
    // 没有课程模板时，不能把唯一可选的系统资源默认藏起来。
    if (courseTemplateGroups.length === 0) {
      setShowOtherTemplates(true);
    }
  }, [courseTemplateGroups.length]);

  const isExpoTemplate = (tpl) => {
    const text = [
      tpl?.displayName,
      tpl?.name,
      tpl?.category,
      tpl?.description,
    ]
      .filter(Boolean)
      .join(" ");
    return /科普月|科技馆|科学讨论|科学圆桌|展示|月球|小卫星|航天|圆桌/.test(text);
  };

  // 切换展示/录课只改大厅布局。已有选中模板（含从「我的课程」带入的
  // initialTemplateId）必须保留，不能被科普启发式模板覆盖。
  const applyDisplayMode = ({ mode, layout, script }) => {
    onDisplayModeChange?.(mode);
    onExpoLayoutChange?.(layout);
    onScriptModeChange?.(script);
    setTab("create");
    if (selectedTemplateId) return;
    const preferredTemplate = templates.find(isExpoTemplate);
    if (!preferredTemplate) return;
    setSelectedTemplateId(preferredTemplate.id);
    setTemplateDetail(null);
    setSelectedCreateRoleName("");
  };

  const switchToExpoMode = () => {
    applyDisplayMode({ mode: "expo", layout: "classic", script: "" });
  };

  const switchToComprehensiveExpoMode = () => {
    applyDisplayMode({ mode: "expo", layout: "comprehensive", script: "" });
  };

  const switchToNormalMode = () => {
    onDisplayModeChange?.("normal");
    onExpoLayoutChange?.("classic");
    onScriptModeChange?.("");
    setTab("create");
  };

  const switchToRecordingMode = () => {
    applyDisplayMode({ mode: "expo", layout: "classic", script: "recording" });
  };

  const pickInitialRoleName = (roles) => {
    if (!Array.isArray(roles) || roles.length === 0) return "";
    return roles[0]?.roleName || "";
  };

  const renderRoleChoices = (roles, selected, onSelect, disabled) => {
    if (!Array.isArray(roles) || roles.length === 0) return null;
    if (roles.length === 1) {
      const only = roles[0];
      return (
        <div className="room-lobby-section">
          <label>{t("groupchat.lobby.select_role")}</label>
          <div className="room-lobby-role-chosen">已选：{only.roleName}</div>
        </div>
      );
    }
    return (
      <div className="room-lobby-section">
        <label>{t("groupchat.lobby.select_role")}</label>
        <div className="room-lobby-role-grid" role="listbox" aria-label={t("groupchat.lobby.select_role")}>
          {roles.map((r) => {
            const active = selected === r.roleName;
            return (
              <button
                key={r.roleName}
                type="button"
                className={`room-lobby-role-chip ${active ? "active" : ""}`}
                onClick={() => onSelect(r.roleName)}
                disabled={disabled}
                aria-pressed={active}
                title={r.roleDesc ? `${r.roleName}：${r.roleDesc}` : r.roleName}
              >
                <strong>{r.roleName}</strong>
                {r.roleDesc ? <span>{r.roleDesc}</span> : null}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const getLanguage = () => getPreferredLanguage();

  const splitProjectName = (projectName) => {
    const raw = String(projectName || "").trim();
    const match = raw.match(/^(.*)（创建者：(.+)）$/);
    if (!match) return { title: raw, creator: "" };
    return { title: match[1].trim(), creator: match[2].trim() };
  };

  const parseServerTime = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    const raw = String(value).trim();
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const getRoomModeLabel = (r) => {
    if (r?.scriptMode === "recording") return "录课模式";
    if (r?.displayMode === "expo") return "展示模式";
    return "普通模式";
  };

  const formatRoomLabel = (r) => {
    const { title, creator } = splitProjectName(r.projectName || r.roomId);
    const name = creator ? `${title} · ${creator}` : title;
    const parts = [name || r.roomId];
    if (r.createdAt) {
      try {
        const d = parseServerTime(r.createdAt);
        if (d) {
          parts.push(d.toLocaleString("zh-CN", {
            timeZone: "Asia/Shanghai",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }));
        }
      } catch (_) {}
    }
    parts.push(`${r.memberCount ?? 0}人`);
    parts.push(getRoomModeLabel(r));
    if (r.totalMessages != null && r.totalMessages > 0) {
      parts.push(`${r.totalMessages}条`);
    }
    return parts.join(" · ");
  };

  const renderRoomLabel = (r) => {
    const { title, creator } = splitProjectName(r.projectName || r.roomId);
    return (
      <span className="room-lobby-room-label">
        <span className="room-lobby-room-title">{title || r.roomId}</span>
        {creator && <span className="room-lobby-room-creator">创建者：{creator}</span>}
        <span className="room-lobby-room-creator">{getRoomModeLabel(r)}</span>
      </span>
    );
  };

  const getRoomStatusLabel = (status) => {
    if (status === "interrupted") return "已中断";
    if (status === "completed") return "已完成";
    if (status === "abandoned") return "已废弃";
    return "历史";
  };

  // 与研讨厅 DiscussionPanel 严格对齐，确保 token 记录与查询使用同一 ID
  const userId = String(
    currentUser?.unified_user_id ||
      currentUser?.userId ||
      currentUser?.studentID ||
      currentUser?.student_id ||
      currentUser?.username ||
      "anonymous"
  );
  // username 用学号/账号，与 GoMAown 查询 training_archives 时的 ts.Username 保持一致
  const username = currentUser?.username || userId;
  // displayName 用真实姓名，通过 creatorName 字段传给 GroupChatHub 用于房间/成员显示
  const displayName = currentUser?.name || username;
  const canUseRecordingMode = [
    currentUser?.name,
    currentUser?.username,
    currentUser?.employeeNumber,
  ].some((v) => recordingWhitelist.includes(String(v || "").trim()));
  const userType =
    currentUser?.user_type ||
    (currentUser?.isSchoolUser ? "campus" : currentUser?.isCasUser ? "bupt_cas" : "guest");
  const orgId = currentUser?.organizationId || currentUser?.org_id || "";
  const assignmentId =
    currentUser?.assignment_id ||
    currentUser?.assignmentId ||
    currentUser?.taskId ||
    currentUser?.trainingTaskId ||
    "";

  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const params = new URLSearchParams();
      if (userId && userId !== "anonymous") params.set("student_id", userId);
      if (orgId) params.set("org_id", orgId);
      if (assignmentId) params.set("assignment_id", assignmentId);
      const query = params.toString();
      const res = await fetch(`${GROUP_CHAT_HUB_HTTP_URL}/groupchat/templates${query ? `?${query}` : ""}`);
      const data = await res.json();
      if (data.templates && data.templates.length > 0) {
        setTemplates(data.templates);
        const preferredId = selectedTemplateId || initialTemplateId;
        if (preferredId && data.templates.some((tpl) => String(tpl.id) === String(preferredId))) {
          setSelectedTemplateId(preferredId);
        } else if (preferredId) {
          setSelectedTemplateId(null);
          setTemplateDetail(null);
          setSelectedCreateRoleName("");
        }
      } else {
        setTemplates([]);
        setSelectedTemplateId(null);
      }
    } catch (e) {
      console.error("[RoomLobby] 获取模板失败:", e);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, [selectedTemplateId, initialTemplateId, userId, orgId, assignmentId]);

  const fetchRooms = useCallback(async () => {
    setLoadingRooms(true);
    try {
      const res = await fetch(`${GROUP_CHAT_HUB_HTTP_URL}/groupchat/rooms`);
      const data = await res.json();
      if (data.rooms && data.rooms.length > 0) {
        setRooms(data.rooms);
      } else {
        setRooms([]);
      }
      setSelectedRoomId(null);
      setRoomDetail(null);
      setSelectedRoleName("");
    } catch (e) {
      console.error("[RoomLobby] 获取房间列表失败:", e);
    } finally {
      setLoadingRooms(false);
    }
  }, []);

  const fetchRoomDetail = useCallback(async (roomId) => {
    if (!roomId) return;
    setLoadingDetail(true);
    try {
      const res = await fetch(`${GROUP_CHAT_HUB_HTTP_URL}/groupchat/rooms/${roomId}`);
      const data = await res.json();
      if (data.roomId) {
        setRoomDetail(data);
        setSelectedRoleName(pickInitialRoleName(data.studentRoles));
      } else {
        setRoomDetail(null);
        setSelectedRoleName("");
      }
    } catch (e) {
      console.error("[RoomLobby] 获取房间详情失败:", e);
      setRoomDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const fetchMyRooms = useCallback(async () => {
    if (!userId || userId === "anonymous") return;
    setLoadingMyRooms(true);
    try {
      const res = await fetch(`${GROUP_CHAT_HUB_HTTP_URL}/groupchat/rooms/my?userId=${encodeURIComponent(userId)}`);
      const data = await res.json();
      setMyRooms(data.rooms || []);
    } catch (e) {
      console.error("[RoomLobby] 获取我的群聊记录失败:", e);
      setMyRooms([]);
    } finally {
      setLoadingMyRooms(false);
    }
  }, [userId]);

  useEffect(() => {
    if (tab === "join") {
      fetchRooms();
    } else if (tab === "history") {
      fetchMyRooms();
    }
  }, [tab, fetchRooms, fetchMyRooms]);

  useEffect(() => {
    if (selectedRoomId) {
      fetchRoomDetail(selectedRoomId);
    } else {
      setRoomDetail(null);
      setSelectedRoleName("");
    }
  }, [selectedRoomId, fetchRoomDetail]);

  useEffect(() => {
    setResumeDisplayMode(roomDetail?.displayMode === "expo" ? "expo" : "normal");
  }, [roomDetail?.displayMode, selectedRoomId]);

  useEffect(() => {
    setJoinViewMode(deriveJoinViewMode(roomDetail));
  }, [roomDetail, selectedRoomId]);

  const fetchTemplateDetail = useCallback(async (tid) => {
    if (!tid) return;
    setLoadingTemplateDetail(true);
    try {
      const res = await fetch(`${GROUP_CHAT_HUB_HTTP_URL}/groupchat/templates/${tid}`);
      const data = await res.json();
      if (data.studentRoles) {
        setTemplateDetail(data);
        setSelectedCreateRoleName(pickInitialRoleName(data.studentRoles));
      } else {
        setTemplateDetail(null);
        setSelectedCreateRoleName("");
      }
    } catch (e) {
      console.error("[RoomLobby] 获取模板详情失败:", e);
      setTemplateDetail(null);
      setSelectedCreateRoleName("");
    } finally {
      setLoadingTemplateDetail(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTemplateId) {
      fetchTemplateDetail(selectedTemplateId);
    } else {
      setTemplateDetail(null);
      setSelectedCreateRoleName("");
    }
  }, [selectedTemplateId, fetchTemplateDetail]);

  const canBindX2 = displayMode === "expo"
    && templateDetail
    && templateDetail.embodimentMode
    && templateDetail.embodimentMode !== "none";

  useEffect(() => {
    if (!canBindX2) {
      setIdleDevices([]);
      setSelectedDeviceId("");
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const resp = await fetch(`${GROUP_CHAT_HUB_HTTP_URL}/groupchat/devices`);
        const data = await resp.json();
        if (cancelled) return;
        setIdleDevices(Array.isArray(data.devices) ? data.devices : []);
      } catch (e) {
        console.error("[RoomLobby] 获取空闲 X2 失败:", e);
        if (!cancelled) setIdleDevices([]);
      }
    };
    load();
    const timer = setInterval(load, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [canBindX2]);

  useEffect(() => {
    if (error && clearError) {
      const t = setTimeout(clearError, 5000);
      return () => clearTimeout(t);
    }
  }, [error, clearError]);

  const selectTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    setTemplateDetail(null);
    setSelectedCreateRoleName("");
  };

  const handleCreateRoom = () => {
    const tid = selectedTemplateId;
    if (!tid) return;
    onCreateRoom({
      templateId: tid,
      userId,
      username,
      displayName,
      creatorName: displayName,
      userType,
      orgId,
      assignmentId,
      lang: getLanguage(),
      roleName: selectedCreateRoleName || "",
      displayMode: displayMode === "expo" ? "expo" : "normal",
      expoLayout: displayMode === "expo" && expoLayout === "comprehensive" ? "comprehensive" : "classic",
      scriptMode: scriptMode === "recording" ? "recording" : "",
      scriptComd: scriptMode === "recording" ? "GKDScript" : "",
      deviceId: canBindX2 ? selectedDeviceId : "",
    });
  };

  const handleJoinRoom = () => {
    if (!selectedRoomId) return;
    const clientViewMode =
      roomDetail?.displayMode === "expo" && joinViewMode !== "normal" ? "expo" : "normal";
    onJoinRoom({
      roomId: selectedRoomId,
      userId,
      username,
      displayName,
      userType,
      roleName: selectedRoleName || "",
      clientViewMode,
      expoLayout: joinViewMode === "comprehensive" ? "comprehensive" : "classic",
    });
  };

  const canCreate =
    Boolean(selectedTemplateId) &&
    (!templateDetail?.studentRoles?.length || selectedCreateRoleName) &&
    !isCreating;
  const canJoin =
    selectedRoomId &&
    roomDetail &&
    roomDetail.canJoin !== false &&
    (roomDetail.studentRoles?.length === 0 || selectedRoleName) &&
    !isJoining;
  const canResume =
    selectedRoomId &&
    roomDetail &&
    roomDetail.canJoin === false &&
    onResumeRoom &&
    (roomDetail.studentRoles?.length === 0 || selectedRoleName) &&
    !isJoining;

  const handleResumeRoom = () => {
    if (!selectedRoomId || !onResumeRoom) return;
    onResumeRoom({
      sourceRoomId: selectedRoomId,
      userId,
      username,
      displayName,
      creatorName: displayName,
      userType,
      orgId,
      lang: getLanguage(),
      roleName: selectedRoleName || "",
      displayMode: resumeDisplayMode === "expo" ? "expo" : "normal",
      expoLayout: resumeDisplayMode === "expo" && expoLayout === "comprehensive" ? "comprehensive" : "classic",
      scriptMode: scriptMode === "recording" ? "recording" : "",
      scriptComd: scriptMode === "recording" ? "GKDScript" : "",
    });
  };

  const handleViewArchive = () => {
    if (!selectedRoomId || !onViewArchive) return;
    onViewArchive(selectedRoomId, roomDetail);
  };

  const renderTemplateResourceCard = () => {
    const tpl = templateDetail || selectedTemplate;
    if (!tpl) return null;
    const roleCount = templateDetail?.studentRoles?.length || 0;
    const phaseCount = templateDetail?.phases?.length || 0;
    const agentCount = templateDetail?.agents?.length || 0;
    const releaseId = tpl.gcTemplateReleaseId || selectedTemplate?.gcTemplateReleaseId;
    return (
      <div className="room-lobby-resource-card">
        <div className="room-lobby-resource-kicker">团队训练模板资源</div>
        <div className="room-lobby-resource-title">{tpl.displayName || tpl.name || `模板 ${tpl.id}`}</div>
        {tpl.description && <div className="room-lobby-resource-desc">{tpl.description}</div>}
        <div className="room-lobby-resource-tags">
          {tpl.category && <span>{tpl.category}</span>}
          {(tpl.minStudents || tpl.maxStudents) && <span>{tpl.minStudents || 2}-{tpl.maxStudents || 6} 人</span>}
          {roleCount > 0 && <span>{roleCount} 个学生角色</span>}
          {phaseCount > 0 && <span>{phaseCount} 个协作阶段</span>}
          {agentCount > 0 && <span>{agentCount} 个智能体</span>}
          {releaseId > 0 && <span>市集版本 #{releaseId}</span>}
        </div>
        {templateDetail?.phases?.length > 0 && (
          <div className="room-lobby-resource-hint">
            先选择你的角色，再按阶段推进：{templateDetail.phases.slice(0, 4).map((p) => p.name || p.title || p.id).filter(Boolean).join(" → ")}
            {templateDetail.phases.length > 4 ? "…" : ""}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="room-lobby">
      <div className="room-lobby-tabs room-lobby-tabs-with-expo">
        <button
          className={`room-lobby-tab ${tab === "create" ? "active" : ""}`}
          onClick={() => setTab("create")}
        >
          {t("groupchat.lobby.create")}
        </button>
        <button
          className={`room-lobby-tab ${tab === "join" ? "active" : ""}`}
          onClick={() => setTab("join")}
        >
          {t("groupchat.lobby.join")}
        </button>
        <button
          className={`room-lobby-tab ${tab === "history" ? "active" : ""}`}
          onClick={() => setTab("history")}
        >
          {t("groupchat.lobby.my_history") || "我的团队训练记录"}
        </button>
        {displayMode !== "expo" ? (
          <button
            type="button"
            className="room-lobby-tab room-lobby-tab-expo"
            onClick={switchToExpoMode}
          >
            展示模式
          </button>
        ) : (
          <button
            type="button"
            className="room-lobby-tab room-lobby-tab-expo active"
            onClick={switchToNormalMode}
          >
            {scriptMode === "recording" ? "退出录课" : "退出展示"}
          </button>
        )}
        {displayMode === "expo" && scriptMode !== "recording" && expoLayout === "comprehensive" ? (
          <button
            type="button"
            className="room-lobby-tab room-lobby-tab-expo"
            onClick={switchToExpoMode}
          >
            切换展示模式
          </button>
        ) : (
          <button
            type="button"
            className={`room-lobby-tab room-lobby-tab-expo ${displayMode === "expo" && expoLayout === "comprehensive" ? "active" : ""}`}
            onClick={switchToComprehensiveExpoMode}
          >
            综合展示模式
          </button>
        )}
        {canUseRecordingMode && scriptMode !== "recording" && (
          <button
            type="button"
            className="room-lobby-tab room-lobby-tab-recording"
            onClick={switchToRecordingMode}
          >
            录课模式
          </button>
        )}
      </div>

      {error && (
        <div className="room-lobby-error">
          {error}
        </div>
      )}

      {tab === "create" && (
        <div className="room-lobby-create">
          <div className="room-lobby-launch-bar">
            <div className="room-lobby-launch-row">
              <div className="room-lobby-launch-selected">
                {selectedTemplate
                  ? `已选：${selectedTemplate.displayName || selectedTemplate.name || `模板 ${selectedTemplate.id}`}`
                  : "请先在下方选择一个训练模板"}
              </div>
              <button
                className="room-lobby-btn room-lobby-btn-primary room-lobby-launch-btn"
                onClick={handleCreateRoom}
                disabled={!canCreate}
              >
                {isCreating
                  ? t("groupchat.creating")
                  : scriptMode === "recording"
                    ? "启动录课模式"
                    : displayMode === "expo"
                    ? "启动展示模式"
                    : t("groupchat.start")}
              </button>
            </div>
            {templateDetail?.studentRoles?.length === 1 && (
              <div className="room-lobby-launch-role-wrap">
                <span>角色</span>
                <span className="room-lobby-role-chosen">{selectedCreateRoleName || templateDetail.studentRoles[0].roleName}</span>
              </div>
            )}
            {canBindX2 && (
              <div className="room-lobby-launch-role-wrap room-lobby-x2-bind">
                <span>现场 X2</span>
                <select
                  value={selectedDeviceId}
                  onChange={(e) => setSelectedDeviceId(e.target.value)}
                  aria-label="选择现场机器人"
                >
                  <option value="">不绑定机器人</option>
                  {idleDevices.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.deviceId}（空闲）
                    </option>
                  ))}
                </select>
                {idleDevices.length === 0 && (
                  <span className="room-lobby-x2-hint">暂无空闲机器人</span>
                )}
              </div>
            )}
            {templateDetail?.studentRoles?.length > 1 && (
              <div className="room-lobby-launch-role-wrap room-lobby-launch-role-wrap--stack">
                <span>角色</span>
                <div className="room-lobby-role-grid" role="listbox" aria-label={t("groupchat.lobby.select_role")}>
                  {templateDetail.studentRoles.map((r) => {
                    const active = selectedCreateRoleName === r.roleName;
                    return (
                      <button
                        key={r.roleName}
                        type="button"
                        className={`room-lobby-role-chip ${active ? "active" : ""}`}
                        onClick={() => setSelectedCreateRoleName(r.roleName)}
                        disabled={loadingTemplateDetail}
                        aria-pressed={active}
                        title={r.roleDesc ? `${r.roleName}：${r.roleDesc}` : r.roleName}
                      >
                        <strong>{r.roleName}</strong>
                        {r.roleDesc ? <span>{r.roleDesc}</span> : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          {renderTemplateResourceCard()}
          <div className="room-lobby-template-picker">
            <div className="room-lobby-template-picker-header">
              <label>{t("groupchat.select_template")}</label>
              <input
                type="search"
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                placeholder="搜索模板、课程或教学场景"
                disabled={loadingTemplates}
                aria-label="搜索项目模板"
              />
            </div>

            {loadingTemplates ? (
              <div className="room-lobby-template-empty">{t("groupchat.lobby.loading")}</div>
            ) : templates.length === 0 ? (
              <div className="room-lobby-template-empty">暂无可用项目模板</div>
            ) : (
              <div className="room-lobby-template-list">
                {courseTemplateGroups.length > 0 ? (
                  <section className="room-lobby-template-scope">
                    <div className="room-lobby-template-scope-title">
                      <span>我的课程模板</span>
                      <small>{new Set(courseTemplateGroups.flatMap(([, group]) => group.map((tpl) => tpl.id))).size} 个</small>
                    </div>
                    {courseTemplateGroups.map(([courseName, group]) => (
                      <div className="room-lobby-template-course-group" key={courseName}>
                        <div className="room-lobby-template-course-name">{courseName}</div>
                        {group.map((tpl) => (
                          <button
                            type="button"
                            className={`room-lobby-template-option ${String(tpl.id) === String(selectedTemplateId) ? "selected" : ""}`}
                            key={tpl.id}
                            onClick={() => selectTemplate(tpl.id)}
                          >
                            <span className="room-lobby-template-option-main">
                              <strong>{tpl.displayName || tpl.name || `模板 ${tpl.id}`}</strong>
                              {tpl.description && <small>{tpl.description}</small>}
                            </span>
                            <span className="room-lobby-template-option-meta">
                              <em>{categoryLabel(tpl.category)}</em>
                              {(tpl.courseScopes || []).length > 1 && <i>适用 {tpl.courseScopes.length} 门课程</i>}
                              <i>{tpl.minStudents || 2}–{tpl.maxStudents || 6} 人</i>
                              {tpl.templateCourses?.length > 0 ? (
                                <small className="room-lobby-template-course-meta" title={tpl.templateCourses.join("、")}>
                                  所属：{tpl.templateCourses.join("、")}
                                </small>
                              ) : (
                                <small className="room-lobby-template-course-meta">所属：未关联课程</small>
                              )}
                            </span>
                          </button>
                        ))}
                      </div>
                    ))}
                  </section>
                ) : (
                  <div className="room-lobby-template-empty">当前已加入课程暂未配置团队协作模板，可从其他可用模板中选择。</div>
                )}

                {otherTemplateGroups.length > 0 && (
                  <section className="room-lobby-template-scope room-lobby-template-scope-other">
                    <button
                      type="button"
                      className="room-lobby-template-other-toggle"
                      onClick={() => {
                        if (showOtherTemplates && selectedTemplate?.scopeType !== "course") {
                          selectTemplate(null);
                        }
                        setShowOtherTemplates((value) => !value);
                      }}
                    >
                      <span>其他可用模板</span>
                      <small>{otherTemplateGroups.reduce((count, [, group]) => count + group.length, 0)} 个 · {showOtherTemplates || normalizedTemplateSearch ? "收起" : "展开"}</small>
                    </button>
                    {(showOtherTemplates || normalizedTemplateSearch) && otherTemplateGroups.map(([category, group]) => (
                      <div className="room-lobby-template-course-group" key={category}>
                        <div className="room-lobby-template-course-name">{category}</div>
                        {group.map((tpl) => (
                          <button
                            type="button"
                            className={`room-lobby-template-option ${String(tpl.id) === String(selectedTemplateId) ? "selected" : ""}`}
                            key={tpl.id}
                            onClick={() => selectTemplate(tpl.id)}
                          >
                            <span className="room-lobby-template-option-main">
                              <strong>{tpl.displayName || tpl.name || `模板 ${tpl.id}`}</strong>
                              {tpl.description && <small>{tpl.description}</small>}
                            </span>
                            <span className="room-lobby-template-option-meta">
                              <em>{tpl.scopeType === "organization" ? "本组织" : tpl.scopeType === "public" ? "系统开放" : "其他可用"}</em>
                              <i>{tpl.minStudents || 2}–{tpl.maxStudents || 6} 人</i>
                              {tpl.templateCourses?.length > 0 ? (
                                <small className="room-lobby-template-course-meta" title={tpl.templateCourses.join("、")}>
                                  所属：{tpl.templateCourses.join("、")}
                                </small>
                              ) : (
                                <small className="room-lobby-template-course-meta">所属：未关联课程</small>
                              )}
                            </span>
                          </button>
                        ))}
                      </div>
                    ))}
                  </section>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "join" && (
        <div className="room-lobby-join">
          <div className="room-lobby-section">
            <label>{t("groupchat.lobby.select_room")}</label>
            <select
              value={selectedRoomId || ""}
              onChange={(e) => setSelectedRoomId(e.target.value || null)}
              disabled={loadingRooms}
            >
              <option value="">
                {loadingRooms ? t("groupchat.lobby.loading") : joinableRooms.length === 0 ? (t("groupchat.lobby.no_joinable") || "暂无可加入的房间") : t("groupchat.lobby.select_room")}
              </option>
              {joinableRooms.map((r) => (
                <option key={r.roomId} value={r.roomId}>
                  {formatRoomLabel(r)}
                </option>
              ))}
            </select>
          </div>

          {renderRoleChoices(
            roomDetail?.studentRoles,
            selectedRoleName,
            setSelectedRoleName,
            loadingDetail
          )}

          {roomDetail?.canJoin !== false && roomDetail?.displayMode === "expo" && (
            <div className="room-lobby-section">
              <label>进入方式</label>
              <select
                value={joinViewMode}
                onChange={(e) => setJoinViewMode(e.target.value)}
                disabled={isJoining}
              >
                <option value="normal">普通聊天界面（非展示）</option>
                <option value="expo">展示舞台</option>
                <option value="comprehensive">综合展示界面</option>
              </select>
              <div className="room-lobby-resource-hint">
                默认与创建者一致。房间仍按展示模式运行，仅切换当前学生看到的界面。
              </div>
            </div>
          )}

          {roomDetail?.canJoin !== false ? (
            <button
              className="room-lobby-btn room-lobby-btn-primary"
              onClick={handleJoinRoom}
              disabled={!canJoin}
            >
              {isJoining ? t("groupchat.lobby.joining") : t("groupchat.lobby.join_room")}
            </button>
          ) : (
            <>
              <div className="room-lobby-section">
                <label>恢复训练模式</label>
                <select
                  value={resumeDisplayMode}
                  onChange={(e) => setResumeDisplayMode(e.target.value)}
                  disabled={isJoining}
                >
                  <option value="normal">普通模式</option>
                  <option value="expo">展示模式</option>
                </select>
                {resumeDisplayMode === "expo" && (
                  <select
                    value={expoLayout}
                    onChange={(e) => onExpoLayoutChange?.(e.target.value)}
                    disabled={isJoining}
                    aria-label="恢复展示界面"
                  >
                    <option value="classic">展示舞台</option>
                    <option value="comprehensive">综合展示界面</option>
                  </select>
                )}
              </div>
              <button
                className="room-lobby-btn room-lobby-btn-primary"
                onClick={handleResumeRoom}
                disabled={!canResume}
              >
                {isJoining ? "恢复中..." : "恢复训练"}
              </button>
            </>
          )}
          {historicalRooms.length > 0 && (
            <div className="room-lobby-history-block">
              <div className="room-lobby-history-title">历史/已中断房间</div>
              <div className="room-lobby-history-list">
                {historicalRooms.slice(0, 8).map((r) => (
                  <button
                    key={r.roomId}
                    type="button"
                    className={`room-lobby-history-item ${selectedRoomId === r.roomId ? "active" : ""}`}
                    onClick={() => setSelectedRoomId(r.roomId)}
                  >
                    {renderRoomLabel(r)}
                    <span className={`room-status-pill status-${r.status || "unknown"}`}>
                      {getRoomStatusLabel(r.status)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "history" && (
        <div className="room-lobby-history">
          <div className="room-lobby-section">
            <label>{t("groupchat.lobby.my_history") || "我参与过的团队训练"}</label>
            {loadingMyRooms ? (
              <div className="room-lobby-loading">{t("groupchat.lobby.loading") || "加载中..."}</div>
            ) : myRooms.length === 0 ? (
              <div className="room-lobby-empty">
                {userId === "anonymous"
                  ? t("groupchat.lobby.login_to_view") || "请登录后查看"
                  : t("groupchat.lobby.no_history") || "暂无团队训练记录"}
              </div>
            ) : (
              <select
                value={selectedRoomId || ""}
                onChange={(e) => {
                  const rid = e.target.value || null;
                  setSelectedRoomId(rid);
                }}
              >
                <option value="">{t("groupchat.lobby.select_room") || "选择团队训练记录"}</option>
                {myJoinableRooms.length > 0 && (
                  <optgroup label="可加入房间">
                    {myJoinableRooms.map((r) => (
                      <option key={r.roomId} value={r.roomId}>
                        {formatRoomLabel(r)}
                      </option>
                    ))}
                  </optgroup>
                )}
                {myHistoricalRooms.length > 0 && (
                  <optgroup label="历史记录">
                    {myHistoricalRooms.map((r) => (
                      <option key={r.roomId} value={r.roomId}>
                        {formatRoomLabel(r)} · {getRoomStatusLabel(r.status)}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            )}
          </div>
          {roomDetail && selectedRoomId && (
            <>
              {renderRoleChoices(
                roomDetail.studentRoles,
                selectedRoleName,
                setSelectedRoleName,
                loadingDetail
              )}
              {roomDetail.canJoin && roomDetail.displayMode === "expo" && (
                <div className="room-lobby-section">
                  <label>进入方式</label>
                  <select
                    value={joinViewMode}
                    onChange={(e) => setJoinViewMode(e.target.value)}
                    disabled={isJoining}
                  >
                    <option value="normal">普通聊天界面（非展示）</option>
                    <option value="expo">展示舞台</option>
                    <option value="comprehensive">综合展示界面</option>
                  </select>
                  <div className="room-lobby-resource-hint">
                    默认与创建者一致。房间仍按展示模式运行，仅切换当前学生看到的界面。
                  </div>
                </div>
              )}
              {!roomDetail.canJoin && (
                <div className="room-lobby-section">
                  <label>恢复训练模式</label>
                  <select
                    value={resumeDisplayMode}
                    onChange={(e) => setResumeDisplayMode(e.target.value)}
                    disabled={isJoining}
                  >
                    <option value="normal">普通模式</option>
                    <option value="expo">展示模式</option>
                  </select>
                  {resumeDisplayMode === "expo" && (
                    <select
                      value={expoLayout}
                      onChange={(e) => onExpoLayoutChange?.(e.target.value)}
                      disabled={isJoining}
                      aria-label="恢复展示界面"
                    >
                      <option value="classic">展示舞台</option>
                      <option value="comprehensive">综合展示界面</option>
                    </select>
                  )}
                </div>
              )}
              {roomDetail.canJoin ? (
                <button
                  className="room-lobby-btn room-lobby-btn-primary"
                  onClick={handleJoinRoom}
                  disabled={!selectedRoleName && roomDetail.studentRoles?.length > 0}
                >
                  {isJoining ? t("groupchat.lobby.joining") : t("groupchat.lobby.join_room")}
                </button>
              ) : (
                <>
                  <button
                    className="room-lobby-btn room-lobby-btn-primary"
                    onClick={handleResumeRoom}
                    disabled={!canResume}
                  >
                    {isJoining ? "恢复中..." : "恢复训练"}
                  </button>
                  <button
                    className="room-lobby-btn"
                    onClick={handleViewArchive}
                    disabled={!onViewArchive}
                  >
                    查看记录
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default RoomLobby;
